import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSessionStorage } from "react-use";
import { useStoreActions } from "app/state/store/hooks";

export const useInitialLoad = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [firstLoadOfSession, setFirstLoadOfSession] = useSessionStorage(
    "first-load-of-session",
    true
  );

  // filter options
  // main
  const fetchLocationFilterOptions = useStoreActions(
    (actions) => actions.ExplorerLocationFilterOptions.fetch
  );
  const fetchPublisherFilterOptions = useStoreActions(
    (actions) => actions.ExplorerPublisherFilterOptions.fetch
  );
  const fetchSectorsFilterOptions = useStoreActions(
    (actions) => actions.ExplorerSectorsFilterOptions.fetch
  );
  const fetchActivityStatusFilterOptions = useStoreActions(
    (actions) => actions.ExplorerActivityStatusFilterOptions.fetch
  );
  const fetchYearFilterOptions = useStoreActions(
    (actions) => actions.ExplorerYearFilterOptions.fetch
  );
  const fetchSDGFilterOptions = useStoreActions(
    (actions) => actions.ExplorerSDGFilterOptions.fetch
  );
  // activity
  const fetchActivityScopeFilterOptions = useStoreActions(
    (actions) => actions.ExplorerActivityScopeFilterOptions.fetch
  );
  const fetchDocumentLinkCategoryFilterOptions = useStoreActions(
    (actions) => actions.ExplorerDocumentLinkCategoryFilterOptions.fetch
  );
  const fetchHierarchyFilterOptions = useStoreActions(
    (actions) => actions.ExplorerHierarchyFilterOptions.fetch
  );
  const fetchHumanitarianScopeVocabularyFilterOptions = useStoreActions(
    (actions) => actions.ExplorerHumanitarianScopeVocabularyFilterOptions.fetch
  );
  const fetchHumanitarianScopeTypeFilterOptions = useStoreActions(
    (actions) => actions.ExplorerHumanitarianScopeTypeFilterOptions.fetch
  );
  const fetchIATIVersionFilterOptions = useStoreActions(
    (actions) => actions.ExplorerIATIVersionFilterOptions.fetch
  );
  const fetchOtherIdentifierTypeFilterOptions = useStoreActions(
    (actions) => actions.ExplorerOtherIdentifierTypeFilterOptions.fetch
  );
  const fetchTagFilterOptions = useStoreActions(
    (actions) => actions.ExplorerTagFilterOptions.fetch
  );
  // default
  const fetchDefaultAidTypeFilterOptions = useStoreActions(
    (actions) => actions.ExplorerDefaultAidTypeFilterOptions.fetch
  );
  const fetchDefaultAidTypeVocabularyFilterOptions = useStoreActions(
    (actions) => actions.ExplorerDefaultAidTypeVocabularyFilterOptions.fetch
  );
  const fetchCurrencyFilterOptions = useStoreActions(
    (actions) => actions.ExplorerCurrencyFilterOptions.fetch
  );
  const fetchDefaultFinanceTypeFilterOptions = useStoreActions(
    (actions) => actions.ExplorerDefaultFinanceTypeFilterOptions.fetch
  );
  const fetchDefaultFlowTypeFilterOptions = useStoreActions(
    (actions) => actions.ExplorerDefaultFlowTypeFilterOptions.fetch
  );
  const fetchDefaultLanguageFilterOptions = useStoreActions(
    (actions) => actions.ExplorerDefaultLanguageFilterOptions.fetch
  );
  const fetchDefaultTiedStatusFilterOptions = useStoreActions(
    (actions) => actions.ExplorerDefaultTiedStatusFilterOptions.fetch
  );
  // organisation
  const fetchTransactionReceiverOrganisationFilterOptions = useStoreActions(
    (actions) =>
      actions.ExplorerTransactionReceiverOrganisationFilterOptions.fetch
  );
  const fetchReportingOrganisationTypeFilterOptions = useStoreActions(
    (actions) => actions.ExplorerReportingOrganisationTypeFilterOptions.fetch
  );
  const fetchCollaborationTypeFilterOptions = useStoreActions(
    (actions) => actions.ExplorerCollaborationTypeFilterOptions.fetch
  );
  // sector
  const fetchSectorVocabularyFilterOptions = useStoreActions(
    (actions) => actions.ExplorerSectorVocabularyFilterOptions.fetch
  );
  const fetchPolicyMarkerFilterOptions = useStoreActions(
    (actions) => actions.ExplorerPolicyMarkerFilterOptions.fetch
  );
  // transaction
  const fetchTransactionFlowTypeFilterOptions = useStoreActions(
    (actions) => actions.ExplorerTransactionFlowTypeFilterOptions.fetch
  );
  const fetchTransactionTiedStatusFilterOptions = useStoreActions(
    (actions) => actions.ExplorerTransactionTiedStatusFilterOptions.fetch
  );
  const fetchTransactionValueCurrencyFilterOptions = useStoreActions(
    (actions) => actions.ExplorerTransactionValueCurrencyFilterOptions.fetch
  );

  // auth
  const fetchUserAction = useStoreActions(
    (actions) => actions.user.authGetFetch
  );
  const fetchStripeSubscriptionPlan = useStoreActions(
    (actions) => actions.StripeSubscriptionPlan.authGetFetch
  );
  const fetchStripePaymentMethod = useStoreActions(
    (actions) => actions.StripePaymentMethod.authGetFetch
  );
  const fetchStripeBillingInfo = useStoreActions(
    (actions) => actions.StripeBillingInfo.authGetFetch
  );
  const fetchStripeInvoices = useStoreActions(
    (actions) => actions.StripeInvoices.authGetFetch
  );
  const fetchUserRoles = useStoreActions(
    (actions) => actions.roles.authGetFetch
  );
  const fetchFavouritesAction = useStoreActions(
    (actions) => actions.GetBookmarks.authGetFetch
  );
  const fetchAlertsAction = useStoreActions(
    (actions) => actions.GetAlerts.authGetFetch
  );

  const fetchExplorerFeaturedFilters = useStoreActions(
    (actions) => actions.ExplorerFeaturedFilters.fetch
  );

  React.useEffect(() => {
    if (!firstLoadOfSession) return;
    setFirstLoadOfSession(false);
    fetchLocationFilterOptions({});
    fetchPublisherFilterOptions({});
    fetchSectorsFilterOptions({});
    fetchActivityStatusFilterOptions({});
    fetchYearFilterOptions({});
    fetchSDGFilterOptions({});
    fetchActivityScopeFilterOptions({});
    fetchDocumentLinkCategoryFilterOptions({});
    fetchHierarchyFilterOptions({});
    fetchHumanitarianScopeVocabularyFilterOptions({});
    fetchHumanitarianScopeTypeFilterOptions({});
    fetchIATIVersionFilterOptions({});
    fetchOtherIdentifierTypeFilterOptions({});
    fetchTagFilterOptions({});
    fetchDefaultAidTypeFilterOptions({});
    fetchDefaultAidTypeVocabularyFilterOptions({});
    fetchCurrencyFilterOptions({});
    fetchDefaultFinanceTypeFilterOptions({});
    fetchDefaultFlowTypeFilterOptions({});
    fetchDefaultLanguageFilterOptions({});
    fetchDefaultTiedStatusFilterOptions({});
    fetchTransactionReceiverOrganisationFilterOptions({});
    fetchReportingOrganisationTypeFilterOptions({});
    fetchCollaborationTypeFilterOptions({});
    fetchSectorVocabularyFilterOptions({});
    fetchPolicyMarkerFilterOptions({});
    fetchTransactionFlowTypeFilterOptions({});
    fetchTransactionTiedStatusFilterOptions({});
    fetchTransactionValueCurrencyFilterOptions({});
    fetchExplorerFeaturedFilters({});
  }, []);

  React.useEffect(() => {
    if (isAuthenticated && user) {
      getAccessTokenSilently().then((token: string) => {
        fetchUserAction({ values: { token } });
        fetchStripeSubscriptionPlan({
          values: { token },
          dontEncryptAuthCall: true,
          routeParams: { id: user?.sub as string },
        });
        fetchStripePaymentMethod({
          values: { token },
          dontEncryptAuthCall: true,
          routeParams: { id: user?.sub as string },
        });
        fetchStripeBillingInfo({
          values: { token },
          dontEncryptAuthCall: true,
          routeParams: { id: user?.sub as string },
        });
        fetchStripeInvoices({
          values: { token },
          dontEncryptAuthCall: true,
          routeParams: { id: user?.sub as string },
        });
        fetchUserRoles({ values: { token } });
        fetchFavouritesAction({
          dontEncryptAuthCall: true,
          values: { token },
          filterString: `filter={"where":{"userId":"${user?.sub}"}}`,
        });
        fetchAlertsAction({
          dontEncryptAuthCall: true,
          values: { token },
          filterString: `filter={"where":{"userId":"${user?.sub}"}}`,
        });
      });
    }
  }, [isAuthenticated]);

  return null;
};
